import React from 'react'
import Container from '../Container/Container';
import AdderColumns from './AdderColumns';
import { ItemTypes, viewerTypes } from '../../constants/constants';
import { useDrop } from 'react-dnd';
import { useStore } from '../../store/store';
import { getContainerById } from '../../store/lookups';
import { firstHiddenAnchorIndex, isInsideLinkedSection } from '../../utils/containers';

const accept = [
  'newBox',
  ItemTypes.CONTAINER_COPIED,
  ItemTypes.CONTAINER_TEMPLATE,
  ItemTypes.IMG,
  ItemTypes.TEXT,
  ItemTypes.SHAPE,
  ItemTypes.LINE,
  ItemTypes.TABLE,
  ItemTypes.TITLE,
  ItemTypes.TITLE_2,
  ItemTypes.TITLE_3,
  ItemTypes.TITLE_4,
  ItemTypes.SUMMARY,
  ItemTypes.MASK,
  ItemTypes.IMG_VARIABLE,
  ItemTypes.TABLE_VARIABLE,
  ItemTypes.ORG_CHART
];

const selector = ({ fromPdf }) => ({
  fromPdf,
})

// Rendu allégé pour la génération PDF : pas de drop target (useDrop), pas
// d'AdderColumns. Le DOM produit est identique au chemin éditeur en mode PDF
// (AdderColumns y est déjà masqué), seule la machinerie interactive disparaît.
const ContainerWrapperPdf = ({ containerId, fromViewer }) => {
  const exists = useStore(({ containers }) => containers.some((c) => c.id === containerId))
  if (!exists) {
    return null
  }
  return (
    <div style={{ position: 'relative' }}>
      <Container
        id={containerId}
        fromViewer={fromViewer}
      />
    </div>
  )
}

const ContainerWrapperInteractive = ({ containerId, onDrop, boxCanDrop, fromViewer, type, titleCollapsed, hideAdder }) => {

  const [{ canDrop, boxIsOver }, dropAdder] = useDrop({
    accept,
    drop: (box, monitor) => onDrop({ box, monitor, isFromAdder: true, insertionIndex: containerIndex }),
    canDrop: boxCanDrop,
    collect: (monitor) => ({
      boxIsOver: monitor.isOver({ shallow: true }),
      canDrop: monitor.canDrop()
    }),
  });
  const { fromPdf } = useStore(selector)
  // Lookup indexé (et pas un `.some`) : ce composant est monté par chaque
  // container du document, un balayage linéaire des lignes à chaque mise à jour
  // du store coûterait O(containers × lignes).
  const isRowContainer = useStore(
    ({ rowContainers }) =>
      !!rowContainers?.length && !!getContainerById(rowContainers, containerId)
  )
  // Sélecteur scalaire : les ancres se lisent dans les boxes, mais ce composant
  // ne doit se re-rendre que si l'index d'insertion change.
  const containerIndex = useStore(({ containers, boxes }) => {
    let baseIdx = containers.findIndex((c) => c.id === containerId)
    if (baseIdx < 0) {
      return baseIdx
    }
    if (containers[baseIdx].from_linked_section) {
      baseIdx = firstHiddenAnchorIndex(containers, boxes, baseIdx)
    }
    if (baseIdx - 1 >= 0) {
      baseIdx -= 1
    } else {
      return null
    }
    return baseIdx
  })
  // Calculé ici plutôt que par l'appelant : l'éditeur et l'aperçu doivent
  // bloquer l'adder exactement aux mêmes endroits.
  const fromLinkedSection = useStore(({ containers, boxes }) =>
    isInsideLinkedSection(
      containers,
      boxes,
      containers.findIndex((c) => c.id === containerId)
    )
  )

  if (containerIndex < 0) {
    // Ligne principale promue en container par le service PDF : l'aperçu la
    // désigne par son id de ligne, absent de `containers`. Elle n'a pas de rang
    // top-level, donc pas d'adder au-dessus — mais elle doit se rendre.
    if (isRowContainer) {
      return (
        <div style={{ position: 'relative' }}>
          <Container id={containerId} fromViewer={fromViewer} />
        </div>
      );
    }
    return null;
  }

  return (
    <div style={{ position: 'relative' }}>
      {!hideAdder && (!fromViewer || fromViewer === viewerTypes.PREVIEW) && !fromPdf && type === 'container' && <div
        ref={dropAdder}
        id={`adder-${containerId}`}>
        <AdderColumns containerIndex={containerIndex}
          boxIsOver={boxIsOver}
          canDrop={canDrop}
          fromLinkedSection={fromLinkedSection}
          titleCollapsed={titleCollapsed}
          disabled={fromLinkedSection || titleCollapsed}
          fromViewer={fromViewer} />
      </div>}
      {(type === 'container' || fromPdf) &&
        <Container
          id={containerId}
          fromViewer={fromViewer}
        />
      }
    </div>
  )
}

const ContainerWrapper = React.memo((props) => {
  const { fromPdf } = useStore(selector)
  if (fromPdf) {
    return <ContainerWrapperPdf containerId={props.containerId} fromViewer={props.fromViewer} />
  }
  return <ContainerWrapperInteractive {...props} />
})

export default ContainerWrapper
